<template>
  <div class="invoice-page">
    <div class="invoice-top">
      <header class="invoice-nav">
        <strong>开票中心</strong>
      </header>
      <nav class="invoice-tabs">
        <button type="button" class="active">开票申请</button>
        <button type="button" @click="openRecords">开票记录</button>
      </nav>
    </div>

    <main v-if="ready">
      <section class="card order-card">
        <div class="section-title"><strong>选择可开票订单</strong><span>已选 {{ selectedOrders.length }} 笔</span></div>
        <div class="search-row">
          <input v-model.trim="keywordInput" maxlength="50" placeholder="搜索订单号或商品名称" @keyup.enter="searchOrders">
          <button @click="searchOrders">搜索</button>
        </div>
        <label class="select-all"><input type="checkbox" :checked="allLoadedSelected" @change="toggleAllLoaded"> 全选</label>
        <div v-for="order in orders" :key="order.orderSn" class="order-item" @click="toggleOrder(order)">
          <input type="checkbox" :checked="isSelected(order.orderSn)" @click.stop @change="toggleOrder(order)">
          <div>
            <strong>{{ order.goodsName || '充值订单' }}</strong>
            <p>{{ order.orderSn }}</p>
          </div>
          <b>¥{{ money(order.orderAmount) }}</b>
        </div>
        <button v-if="nextCursor" class="load-more" :disabled="ordersLoading" @click="loadOrders(false)">{{ ordersLoading ? '加载中...' : '加载更多' }}</button>
        <p v-if="!orders.length && !ordersLoading" class="empty">暂无可开票订单</p>
        <div class="amount-summary"><span>已选 {{ selectedOrders.length }} 笔，可开票金额</span><strong>¥{{ money(totalAmount) }}</strong></div>
      </section>

      <section class="card form-card">
        <div class="section-title">
          <strong>{{ form.invoice_type === 2 ? '公司发票信息' : '个人发票信息' }}</strong>
          <button v-if="form.invoice_type === 2" type="button" class="link-button" @click="changeType(1)">返回个人开票</button>
        </div>
        <label>{{ form.invoice_type === 2 ? '企业名称' : '发票抬头' }}<span>*</span></label>
        <input v-model.trim="form.invoice_title" :readonly="form.invoice_type === 1" maxlength="100" :placeholder="form.invoice_type === 2 ? '请输入营业执照上的企业全称' : '请输入发票抬头'">
        <template v-if="form.invoice_type === 2">
          <label>纳税人识别号<span>*</span></label>
          <input v-model="form.buyer_tax_no" maxlength="20" placeholder="请输入1-20位字母或数字">
          <label>注册地址</label>
          <input v-model.trim="form.buyer_address" maxlength="200" placeholder="请输入注册地址">
          <label>注册电话</label>
          <input v-model.trim="form.buyer_phone" maxlength="30" placeholder="请输入注册电话">
          <label>开户银行</label>
          <input v-model.trim="form.buyer_bank_name" maxlength="100" placeholder="请输入开户银行">
          <label>银行账号</label>
          <input v-model.trim="form.buyer_bank_account" maxlength="32" placeholder="请输入银行账号">
        </template>
        <label>发票类型</label>
        <input value="增值税电子普通发票" readonly>
        <label>发票内容</label>
        <input :value="bootstrap.tax_item_name" readonly>
        <label>收票邮箱<span>*</span></label>
        <input v-model.trim="form.receiver_email" maxlength="100" type="email" placeholder="请输入收票邮箱">
        <label>是否分订单开票<span>*</span></label>
        <div class="radio-row">
          <button :class="{ active: form.split_by_order === 0 }" @click="form.split_by_order = 0">否</button>
          <button :class="{ active: form.split_by_order === 1 }" @click="form.split_by_order = 1">是</button>
        </div>

        <div v-if="form.invoice_type === 2 && selectedEntities.length" class="proof-materials">
          <div class="section-title"><strong>公司证明材料</strong><a href="/公司抬头发票证明材料模板.docx" download @click.prevent="downloadProofTemplate">下载模板</a></div>
          <p class="hint">每个收款主体必须且只能选择 1 个文件，点击提交后才会上传。支持 JPG、JPEG、PNG、PDF，单个不超过 10MB。</p>
          <div v-for="entity in selectedEntities" :key="entity.id" class="proof-item">
            <strong>{{ entity.name }}</strong>
            <van-uploader
              class="proof-uploader"
              accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
              result-type="file"
              :max-count="1"
              :max-size="proofFileMaxSize"
              :preview-image="false"
              :after-read="fileItem => chooseFile(entity, fileItem)"
              @oversize="rejectOversizeFile(entity, $event)"
            >
              <button type="button" class="file-button" :title="files[entity.id] ? files[entity.id].name : '选择文件'">{{ files[entity.id] ? files[entity.id].name : '选择文件' }}</button>
            </van-uploader>
          </div>
        </div>
      </section>

      <p v-if="form.invoice_type === 1" class="invoice-notice">
        发票开具完成后，将直接发送到您邮箱。若您有实际业务情形需要使用企业主体信息作为开票抬头进行票据开具的，可根据<button type="button" class="notice-link" @click="changeType(2)">公司发票</button>页面展示的业务流程完成后续相关办理流程。
      </p>
      <p v-else class="invoice-notice">发票开具完成后，将直接发送到您邮箱。</p>

      <section class="summary">
        <button :disabled="submitting" @click="submit">{{ submitting ? submitText : '提交申请' }}</button>
      </section>
    </main>
    <div v-else class="loading">加载中...</div>

    <div v-if="personalConfirmVisible" class="consumer-confirm-layer" role="dialog" aria-modal="true" aria-labelledby="personalConfirmTitle">
      <section class="consumer-confirm-modal">
        <header class="consumer-confirm-head"><h2 id="personalConfirmTitle">确认提交开票</h2></header>
        <div class="consumer-confirm-content">
          <article class="declaration-letter" aria-labelledby="declarationLetterTitle">
            <h3 id="declarationLetterTitle">声明函</h3>
            <p class="declaration-recipient">致：{{ declarationRecipient }}</p>
            <div class="declaration-body">
              <p class="declaration-body-label">兹声明：</p>
              <p>本人系该游戏账号实际所有人，该账号所涉充值款项均系本人支付。</p>
              <p>现特向贵司申请，就本次提交申请的订单开具增值税发票，恳请贵司予以办理。</p>
              <p>本人知悉并同意，本次申请订单发票抬头为该游戏账号实名认证人。贵司开具该笔订单发票后，本人确认不再就该充值款项向贵司另行主张权利。</p>
              <p>本人承诺以上声明内容真实、准确、完整，如有不实，本人愿承担一切法律责任及由此产生的全部后果。</p>
              <p>特此声明。</p>
            </div>
            <div class="declaration-signature">
              <p>声明人：{{ bootstrap.real_name }}</p>
              <p>日期：{{ declarationDate }}</p>
            </div>
          </article>
        </div>
        <footer class="consumer-confirm-footer">
          <button type="button" class="confirm-cancel" @click="cancelPersonalConfirm">取消</button>
          <button type="button" class="confirm-submit" :disabled="personalConfirmCountdown > 0" @click="confirmPersonalSubmit">
            {{ personalConfirmCountdown > 0 ? '确定提交（' + personalConfirmCountdown + 's）' : '确定提交' }}
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import { Toast, Uploader } from "vant";
import { v4 as uuidv4 } from "uuid";
import { callHideToolbar, phoneBackButtonListener } from "@/bridge/AppJsBridge";
import { createInvoiceApplication, getInvoiceBootstrap, getInvoiceOrders, getInvoiceRecordDetail, uploadInvoiceProof } from "@/apis/invoiceCenter";
import { consumeInvoiceSession } from "./session";
import { closeInvoiceCenter, formatMoney, handleInvoiceError, openInvoiceExternalLink } from "./common";
import { getInvoiceType, setInvoiceTrackingContext, trackInvoiceEvent } from "./tracking";

const MIN_INVOICE_TASK_AMOUNT_CENTS = 2;

const hasValidInvoiceTaskAmounts = (orders, splitByOrder) => {
  const amounts = {};
  orders.forEach(order => {
    const key = splitByOrder ? String(order.orderSn) : String(order.invoice_entity_id);
    amounts[key] = (amounts[key] || 0) + Math.round(Number(order.orderAmount || 0) * 100);
  });
  return Object.keys(amounts).every(key => amounts[key] >= MIN_INVOICE_TASK_AMOUNT_CENTS);
};

export default {
  components: { "van-uploader": Uploader },
  data() {
    return {
      session: "",
      ready: false,
      bootstrap: {},
      companyInvoiceTitle: "",
      form: {
        invoice_type: 1,
        invoice_title: "",
        buyer_tax_no: "",
        buyer_address: "",
        buyer_phone: "",
        buyer_bank_name: "",
        buyer_bank_account: "",
        receiver_email: "",
        split_by_order: 0,
      },
      orders: [],
      selected: {},
      files: {},
      nextCursor: "",
      keyword: "",
      keywordInput: "",
      ordersLoading: false,
      submitting: false,
      submitText: "提交中...",
      personalConfirmVisible: false,
      personalConfirmCountdown: 10,
      personalConfirmTimer: null,
      personalConfirmResolve: null,
      declarationDate: "",
      clientRequestId: "",
      resubmitRequestNo: "",
      resubmitOrderSns: [],
      proofFileMaxSize: 10 * 1024 * 1024,
    };
  },
  computed: {
    selectedOrders() {
      return this.orders.filter(order => this.selected[String(order.orderSn)]);
    },
    selectedEntities() {
      const entities = {};
      this.selectedOrders.forEach(order => {
        if (order.invoice_entity_id) entities[order.invoice_entity_id] = { id: order.invoice_entity_id, name: order.entity_name || "收款主体" };
      });
      return Object.keys(entities).map(key => entities[key]);
    },
    totalAmount() {
      return this.selectedOrders.reduce((sum, order) => sum + Number(order.orderAmount || 0), 0);
    },
    allLoadedSelected() {
      const selectable = this.orders.filter(order => order.selectable);
      return selectable.length > 0 && selectable.every(order => this.isSelected(order.orderSn));
    },
    declarationRecipient() {
      return this.selectedEntities.map(entity => entity.name).join("、") || "相关收款主体";
    },
  },
  async created() {
    this.session = consumeInvoiceSession(this.$route);
    callHideToolbar(true);
    phoneBackButtonListener(() => this.closePage());
    if (!this.session) return handleInvoiceError({ code: 50001 });
    try {
      this.bootstrap = await getInvoiceBootstrap(this.session);
      setInvoiceTrackingContext(this.session, this.bootstrap);
      this.form.invoice_title = this.bootstrap.real_name || "";
      this.resubmitRequestNo = this.$route.query.resubmit || "";
      if (this.resubmitRequestNo) await this.loadResubmit();
      await this.loadOrders(true, Boolean(this.resubmitRequestNo));
      this.ready = true;
      this.$nextTick(() => trackInvoiceEvent("INVOICE_CENTER_SHOW", {
        invoice_type: getInvoiceType(this.form.invoice_type),
        available_order_count: this.orders.filter(order => order.selectable).length,
      }));
    } catch (error) {
      handleInvoiceError(error);
    }
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    this.closePersonalConfirm(false);
    callHideToolbar(false);
  },
  methods: {
    money: formatMoney,
    selectedTrackingData() {
      return {
        invoice_type: getInvoiceType(this.form.invoice_type),
        selected_order_count: this.selectedOrders.length,
        selected_amount: Number(formatMoney(this.totalAmount)),
      };
    },
    closePage() {
      trackInvoiceEvent("INVOICE_CENTER_CLOSE_CLICK", { from_page: "invoice_apply" });
      closeInvoiceCenter();
    },
    openRecords() {
      trackInvoiceEvent("INVOICE_RECORD_TAB_CLICK", { from_page: "invoice_apply" });
      this.$router.push("/gameJump/invoiceCenter/records");
    },
    changeType(type) {
      if (this.form.invoice_type === type) return;
      const previousType = this.form.invoice_type;
      if (previousType === 2) this.companyInvoiceTitle = this.form.invoice_title;
      this.form.invoice_type = type;
      if (type === 1) {
        this.form.invoice_title = this.bootstrap.real_name || "";
      } else {
        this.form.invoice_title = this.companyInvoiceTitle;
      }
      trackInvoiceEvent("INVOICE_TYPE_CHANGE", {
        previous_invoice_type: getInvoiceType(previousType),
        invoice_type: getInvoiceType(type),
      });
    },
    async loadResubmit() {
      const detail = await getInvoiceRecordDetail(this.session, this.resubmitRequestNo);
      if (!detail.can_resubmit) throw new Error("该申请当前不可重新提交");
      this.form.invoice_type = 2;
      this.form.invoice_title = detail.invoice_title || "";
      this.companyInvoiceTitle = this.form.invoice_title;
      this.form.buyer_tax_no = detail.buyer_tax_no || "";
      this.form.buyer_address = detail.buyer_address || "";
      this.form.buyer_phone = detail.buyer_phone || "";
      this.form.buyer_bank_name = detail.buyer_bank_name || "";
      this.form.buyer_bank_account = detail.buyer_bank_account || "";
      this.form.receiver_email = detail.receiver_email || "";
      this.form.split_by_order = Number(detail.split_by_order || 0);
      this.resubmitOrderSns = (detail.orders || []).map(order => String(order.order_sn));
    },
    async loadOrders(reset, loadAll) {
      if (this.ordersLoading) return;
      this.ordersLoading = true;
      try {
        if (reset) {
          this.orders = [];
          this.nextCursor = "";
          this.selected = {};
        }
        do {
          const requestCursor = this.nextCursor;
          const page = await getInvoiceOrders(this.session, { cursor: requestCursor || undefined, page_size: 1000, keyword: this.keyword || undefined });
          const list = (page.list || []).filter(order => order.selectable);
          this.orders = this.orders.concat(list);
          this.nextCursor = page.next_cursor || "";
          if (this.resubmitOrderSns.length) {
            list.forEach(order => {
              if (this.resubmitOrderSns.indexOf(String(order.orderSn)) >= 0) this.$set(this.selected, String(order.orderSn), true);
            });
          }
          if (!loadAll && list.length) break;
          if (!this.nextCursor || this.nextCursor === requestCursor) break;
        } while (this.nextCursor);
      } finally {
        this.ordersLoading = false;
      }
    },
    async searchOrders() {
      this.keyword = this.keywordInput;
      try {
        await this.loadOrders(true, false);
        trackInvoiceEvent("INVOICE_ORDER_SEARCH", {
          search_keyword: this.keyword,
          result_count: this.orders.length,
        });
      } catch (error) {
        handleInvoiceError(error);
      }
    },
    onScroll() {
      const root = document.documentElement;
      if (this.nextCursor && !this.ordersLoading && root.scrollTop + window.innerHeight >= root.scrollHeight - 120) {
        this.loadOrders(false, false).catch(handleInvoiceError);
      }
    },
    isSelected(orderSn) { return Boolean(this.selected[String(orderSn)]); },
    toggleOrder(order) {
      if (!order.selectable) return;
      const selected = !this.isSelected(order.orderSn);
      this.$set(this.selected, String(order.orderSn), selected);
      trackInvoiceEvent("INVOICE_ORDER_SELECT", {
        action: selected ? "select" : "deselect",
        order_id: String(order.orderSn),
        ...this.selectedTrackingData(),
      });
    },
    toggleAllLoaded(event) {
      const checked = event.target.checked;
      this.orders.forEach(order => { if (order.selectable) this.$set(this.selected, String(order.orderSn), checked); });
      trackInvoiceEvent("INVOICE_ORDER_SELECT", {
        action: checked ? "select_all" : "deselect_all",
        ...this.selectedTrackingData(),
      });
    },
    downloadProofTemplate() {
      trackInvoiceEvent("INVOICE_PROOF_TEMPLATE_DOWNLOAD", { invoice_type: getInvoiceType(this.form.invoice_type) });
      if (!openInvoiceExternalLink("/公司抬头发票证明材料模板.docx")) Toast.fail("模板地址无效");
    },
    chooseFile(entity, fileItem) {
      const file = fileItem && fileItem.file;
      if (!file) return;
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const trackingData = {
        file_type: ext,
        file_size: file.size,
        company_entity: entity.name,
      };
      if (["jpg", "jpeg", "png", "pdf"].indexOf(ext) < 0) {
        trackInvoiceEvent("INVOICE_PROOF_UPLOAD", { ...trackingData, result: "fail", error_message: "unsupported_file_type" });
        return Toast.fail("仅支持 JPG、JPEG、PNG、PDF 文件");
      }
      if (file.size > 10 * 1024 * 1024) {
        trackInvoiceEvent("INVOICE_PROOF_UPLOAD", { ...trackingData, result: "fail", error_message: "file_too_large" });
        return Toast.fail("单个文件不能超过 10MB");
      }
      this.$set(this.files, entity.id, file);
      trackInvoiceEvent("INVOICE_PROOF_UPLOAD", { ...trackingData, result: "success" });
    },
    rejectOversizeFile(entity, fileItem) {
      const file = fileItem && fileItem.file;
      if (!file) return;
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      trackInvoiceEvent("INVOICE_PROOF_UPLOAD", {
        file_type: ext,
        file_size: file.size,
        company_entity: entity.name,
        result: "fail",
        error_message: "file_too_large",
      });
      Toast.fail("单个文件不能超过 10MB");
    },
    openPersonalSubmitConfirm() {
      this.closePersonalConfirm(false);
      this.declarationDate = this.bootstrap.server_date;
      this.personalConfirmCountdown = 10;
      this.personalConfirmVisible = true;
      return new Promise(resolve => {
        this.personalConfirmResolve = resolve;
        this.personalConfirmTimer = window.setInterval(() => {
          this.personalConfirmCountdown -= 1;
          if (this.personalConfirmCountdown <= 0) {
            this.personalConfirmCountdown = 0;
            window.clearInterval(this.personalConfirmTimer);
            this.personalConfirmTimer = null;
          }
        }, 1000);
      });
    },
    cancelPersonalConfirm() {
      this.closePersonalConfirm(false);
    },
    confirmPersonalSubmit() {
      if (this.personalConfirmCountdown > 0) return;
      this.closePersonalConfirm(true);
    },
    closePersonalConfirm(confirmed) {
      if (this.personalConfirmTimer) window.clearInterval(this.personalConfirmTimer);
      this.personalConfirmTimer = null;
      this.personalConfirmVisible = false;
      const resolve = this.personalConfirmResolve;
      this.personalConfirmResolve = null;
      if (resolve) resolve(Boolean(confirmed));
    },
    validate() {
      if (!this.selectedOrders.length) return "请至少选择一笔订单";
      if (this.form.split_by_order === 1 && this.selectedOrders.length > 100) return "分订单开票单次最多选择 100 笔订单";
      if (!hasValidInvoiceTaskAmounts(this.selectedOrders, this.form.split_by_order === 1)) return "每个开票任务金额不得低于0.02元，请重新选择订单";
      if (!this.form.invoice_title) return "请输入发票抬头";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.receiver_email)) return "请输入正确的收票邮箱";
      if (this.form.invoice_type === 1 && !this.bootstrap.server_date) return "服务器日期获取失败，请刷新页面重试";
      if (this.form.invoice_type === 2) {
        if (!/^[A-Za-z0-9]{1,20}$/.test(this.form.buyer_tax_no)) return "纳税人识别号仅支持1-20位字母或数字";
        const missing = this.selectedEntities.find(entity => !this.files[entity.id]);
        if (missing) return "请为“" + missing.name + "”选择证明材料";
      }
      return "";
    },
    buildPayload() {
      return {
        ...this.form,
        client_request_id: this.clientRequestId,
        invoice_config_version: this.bootstrap.invoice_config_version,
        order_sns: this.selectedOrders.map(order => String(order.orderSn)),
        resubmit_from_request_no: this.resubmitRequestNo || undefined,
      };
    },
    async uploadFiles(payload) {
      const queue = this.selectedEntities.slice();
      const results = [];
      const worker = async () => {
        while (queue.length) {
          const entity = queue.shift();
          this.submitText = "正在上传材料...";
          const data = new FormData();
          Object.keys(payload).forEach(key => {
            if (key === "order_sns") payload.order_sns.forEach(value => data.append("order_sns[]", value));
            else if (payload[key] !== undefined) data.append(key, payload[key]);
          });
          data.append("invoice_entity_id", entity.id);
          data.append("file", this.files[entity.id]);
          results.push(await uploadInvoiceProof(this.session, data));
        }
      };
      await Promise.all([worker(), worker()]);
      return results.map(item => ({ invoice_entity_id: item.invoice_entity_id, upload_id: item.upload_id }));
    },
    async submit() {
      const message = this.validate();
      trackInvoiceEvent("INVOICE_SUBMIT_CLICK", {
        ...this.selectedTrackingData(),
        validation_result: message ? "fail" : "success",
        validation_error: message || undefined,
      });
      if (message) return Toast.fail(message);
      if (this.form.invoice_type === 1) {
        const confirmed = await this.openPersonalSubmitConfirm();
        if (!confirmed) return;
      }
      this.submitting = true;
      this.clientRequestId = this.clientRequestId || uuidv4();
      let applicationRequested = false;
      try {
        const payload = this.buildPayload();
        if (payload.invoice_type === 2) payload.proof_uploads = await this.uploadFiles(payload);
        this.submitText = "正在提交申请...";
        applicationRequested = true;
        const result = await createInvoiceApplication(this.session, payload);
        trackInvoiceEvent("INVOICE_SUBMIT_RESULT", {
          ...this.selectedTrackingData(),
          request_no: result.request_no,
          result: "success",
          request_status: result.status_code || result.status,
          request_status_name: result.status,
        });
        this.$router.replace("/gameJump/invoiceCenter/result/" + result.request_no);
      } catch (error) {
        if (applicationRequested) {
          trackInvoiceEvent("INVOICE_SUBMIT_RESULT", {
            ...this.selectedTrackingData(),
            result: "fail",
            error_code: error && error.code,
            error_message: (error && error.message) || "request_failed",
          });
        }
        handleInvoiceError(error);
      } finally {
        this.submitting = false;
        this.submitText = "提交中...";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.invoice-page { min-height: 100%; background: #f5f6f8; color: #222; padding-bottom: 84px; font-size: 14px; }
.invoice-top { position: sticky; top: 0; z-index: 5; background: #fff; }
.invoice-nav { height: 58px; display: flex; align-items: center; justify-content: space-between; padding: 0 18px; }
.invoice-nav strong { font-size: 22px; }
.invoice-nav button { border: 0; background: transparent; color: #555; font-size: 28px; line-height: 1; }
.invoice-tabs { display: flex; height: 48px; border-bottom: 1px solid #eee; }
.invoice-tabs button { position: relative; flex: 1; border: 0; color: #777; background: #fff; font-size: 16px; }
.invoice-tabs button.active { color: #1677ff; font-weight: 600; }
.invoice-tabs button.active::after { position: absolute; right: 28%; bottom: 0; left: 28%; height: 2px; content: ""; background: #1677ff; }
.card { margin: 12px; padding: 16px; background: #fff; border-radius: 10px; }
.radio-row { display: flex; gap: 10px; }
.radio-row button { flex: 1; height: 38px; border: 1px solid #ddd; border-radius: 6px; background: #fff; }
.radio-row button.active { color: #3978f6; border-color: #3978f6; background: #eef4ff; }
.form-card label { display: block; margin: 14px 0 7px; font-weight: 600; }
.form-card label span { color: #ee4040; }
.form-card input, .search-row input { box-sizing: border-box; width: 100%; height: 42px; padding: 0 12px; border: 1px solid #ddd; border-radius: 6px; outline: none; }
.form-card input[readonly] { background: #f7f7f7; color: #777; }
.section-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.section-title span, .section-title a { color: #3978f6; font-size: 13px; }
.link-button, .notice-link { padding: 0; border: 0; color: #3978f6; background: transparent; font-size: 13px; }
.notice-link { margin: 0 2px; color: inherit; font: inherit; line-height: inherit; vertical-align: baseline; text-decoration: underline; }
.search-row { display: flex; gap: 8px; }
.search-row button, .load-more { min-width: 64px; border: 0; border-radius: 6px; color: #fff; background: #3978f6; }
.select-all { display: block; padding: 14px 0 8px; color: #555; }
.order-item { display: flex; align-items: center; gap: 10px; padding: 13px 0; border-top: 1px solid #eee; }
.order-item div { min-width: 0; flex: 1; }
.order-item strong, .order-item p { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.order-item p { margin: 5px 0 0; color: #999; font-size: 12px; }
.order-item b { color: #f25b3d; }
.load-more { width: 100%; height: 38px; margin-top: 10px; }
.amount-summary { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; padding-top: 14px; border-top: 1px solid #eee; }
.amount-summary span { color: #888; }
.amount-summary strong { color: #f25b3d; font-size: 20px; }
.proof-materials { margin-top: 18px; padding-top: 16px; border-top: 1px solid #eee; }
.hint { color: #888; line-height: 1.6; font-size: 12px; }
.proof-item { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; border-top: 1px solid #eee; }
.proof-item strong { flex: 1; }
.proof-uploader { min-width: 0; max-width: 48%; }
.proof-uploader ::v-deep .van-uploader__input-wrapper { max-width: 100%; }
.file-button { display: block; max-width: 100%; padding: 8px 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; border: 1px solid #3978f6; border-radius: 5px; color: #3978f6; background: #fff; }
.invoice-notice { margin: 0 16px 12px; color: #888; line-height: 1.6; font-size: 12px; }
.consumer-confirm-layer { position: fixed; inset: 0; z-index: 20; display: flex; align-items: center; justify-content: center; padding: 10px; background: rgba(23,30,40,.55); }
.consumer-confirm-modal { display: flex; width: 100%; max-width: 520px; max-height: calc(100vh - 20px); flex-direction: column; overflow: hidden; border-radius: 10px; background: #fff; box-shadow: 0 14px 38px rgba(14,20,30,.28); }
.consumer-confirm-head { min-height: 62px; display: flex; flex: 0 0 auto; align-items: center; padding: 0 22px; border-bottom: 1px solid #eee; }
.consumer-confirm-head h2 { margin: 0; font-size: 20px; }
.consumer-confirm-content { min-height: 0; flex: 1 1 auto; overflow-y: auto; padding: 24px 28px 28px; }
.declaration-letter { color: #30343b; font-size: 15px; line-height: 2; }
.declaration-letter h3 { margin: 0 0 26px; text-align: center; font-size: 22px; }
.declaration-letter p { margin: 0 0 16px; }
.declaration-recipient { margin-bottom: 28px !important; overflow-wrap: anywhere; }
.declaration-body-label { font-weight: 600; }
.declaration-body p:not(.declaration-body-label) { text-indent: 2em; }
.declaration-signature { width: max-content; max-width: 100%; margin: 32px 0 0 auto; }
.declaration-signature p { margin-bottom: 8px; overflow-wrap: anywhere; }
.consumer-confirm-footer { min-height: 72px; display: flex; flex: 0 0 auto; align-items: center; justify-content: flex-end; gap: 12px; padding: 0 22px; border-top: 1px solid #eee; }
.consumer-confirm-footer button { min-width: 92px; height: 44px; padding: 0 18px; border-radius: 5px; font-size: 15px; }
.confirm-cancel { border: 1px solid #ddd; color: #555; background: #fff; }
.confirm-submit { min-width: 160px !important; border: 1px solid #3978f6; color: #fff; background: #3978f6; }
.confirm-submit:disabled { border-color: #8fc1fa; background: #8fc1fa; }
.summary { position: fixed; right: 0; bottom: 0; left: 0; z-index: 4; padding: 10px 14px; background: #fff; box-shadow: 0 -2px 10px rgba(0,0,0,.08); }
.summary button { width: 100%; height: 44px; border: 0; border-radius: 22px; color: #fff; background: #3978f6; }
.summary button:disabled { opacity: .6; }
.empty, .loading { padding: 50px 0; text-align: center; color: #999; }
</style>
